import { useState, useEffect } from "react";
import { Brain, Globe, CheckCircle, Lock } from "lucide-react";
import useLocalization from '../../../../hooks/useLocalization';
import { selectIsTextFlowLocked, selectSelectedChatId } from "../../../../store/features/message/textContentSelectors";
import { useSelector } from "react-redux";
import InfoTooltip from "../../Tooltip/InfoTooltip";
import PropTypes from "prop-types";



const LanguageDetector = ({
    isLanguageDetecting,
    languageDetectedMessage,
}) => {
    const [status, setStatus] = useState("idle");
    const [dots, setDots] = useState("");
    const { t } = useLocalization();
    const selectedChatId = useSelector(selectSelectedChatId);
    const isTextFlowLocked = useSelector(selectIsTextFlowLocked(selectedChatId));

    const STAGES = {
        analyzing: {
            icon: <Brain className="w-4 h-4 text-blue-500 animate-pulse" />,
            text: (dots) => `${t('languageDetetors_analyzing')}${dots}`,
            progressWidth: "30%",
        },
        processing: {
            icon: <Globe className="w-4 h-4 text-purple-500 animate-spin" />,
            text: (dots) => `${t('languageDetetors_processing')}${dots}`,
            progressWidth: "70%",
        },
        completed: {
            icon: <CheckCircle className="w-4 h-4 text-green-500" />,
            text: (msg) => msg || "Language detected",
        },
        locked: {
            icon: <Lock className="w-4 h-4 text-amber-500" />,
            text: (msg) => msg || "Using previously detected language",
        },
    };

    useEffect(() => {
        // If text flow is locked, show locked state immediately
        if (isTextFlowLocked && languageDetectedMessage) {
            setStatus("locked");
            setDots("");
            return;
        }

        if (isLanguageDetecting) {
            setStatus("analyzing");

            const dotsInterval = setInterval(() => {
                setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
            }, 500);

            const stageTimeout = setTimeout(() => setStatus("processing"), 1000);

            return () => {
                clearInterval(dotsInterval);
                clearTimeout(stageTimeout);
            };
        }

        if (languageDetectedMessage && !isTextFlowLocked) {
            setStatus("completed");
        } else {
            setStatus("idle");
        }
        setDots("");
    }, [isLanguageDetecting, languageDetectedMessage, isTextFlowLocked]);

    if (status === "idle") return null;

    const stageConfig = STAGES[status];
    if (!stageConfig) return null;

    const showTooltip = status === "completed" || status === "locked";
    return (
        <div className={`mb-2 transition-all duration-500 ${status === "completed" || status === "locked" ? "scale-100" : "scale-98"}`}>
            <div className={`relative rounded-lg transition-all duration-300 ${status === "analyzing" || status === "processing" ? "animate-pulse" : ""}`}>
                {(status === "analyzing" || status === "processing") && (
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                )}

                <div className="relative flex items-center gap-3">
                    {/* <InfoTooltip text="This language will be used for entire conversation untill final categorization and submission of grievance." direction="top">
                        <div className="flex-shrink-0">{stageConfig.icon}</div>
                    </InfoTooltip> */}
                    {showTooltip ? (
                        <InfoTooltip
                            text="Language stays active till submission."
                            direction="top-left"
                        >
                            <div className="flex-shrink-0">{stageConfig.icon}</div>
                        </InfoTooltip>
                    ) : (
                        <div className="flex-shrink-0">{stageConfig.icon}</div>
                    )}
                    <div className="flex-1">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {/* ✅ FIX: Pass the correct argument based on status */}
                            {status === "analyzing" || status === "processing"
                                ? stageConfig.text(dots)  // Pass dots for analyzing/processing
                                : stageConfig.text(languageDetectedMessage)  // Pass message for completed/locked
                            }
                        </div>
                        {(status === "analyzing" || status === "processing") && (
                            <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse"
                                    style={{ width: stageConfig.progressWidth }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

LanguageDetector.propTypes = {
    isLanguageDetecting: PropTypes.bool.isRequired,
    languageDetectedMessage: PropTypes.string,
};

export default LanguageDetector;